import React from "react";
import {
  Alert,
  Box,
  Button,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
  IconButton,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { getLanguageLabel } from "../../utils/languageUtils";

import PublicIcon from "@mui/icons-material/Public";
import PersonIcon from "@mui/icons-material/Person";
import TranslateIcon from "@mui/icons-material/Translate";
import GroupsIcon from "@mui/icons-material/Groups";
import BlockIcon from "@mui/icons-material/Block";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { AssociatedCampaignTarget } from "../../store/apis/briefingApi";

interface AssociatedCampaignTargetSectionProps {
  target: AssociatedCampaignTarget | null;
  numberFormatter: Intl.NumberFormat;
  onCreateTarget?: () => void;
  onEditTarget?: () => void;
  onDeleteTarget?: () => void;
  isEditable?: boolean;
}

const AssociatedCampaignTargetSection: React.FC<
  AssociatedCampaignTargetSectionProps
> = ({
  target,
  numberFormatter,
  onCreateTarget,
  onEditTarget,
  onDeleteTarget,
  isEditable = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (!target) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.grey[200]}`,
          p: { xs: 2, md: 4 },
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Typography variant="h6" fontWeight={700}>
            {t("briefingsView.targetSectionTitle", "Audience targeting")}
          </Typography>
          {isEditable && (
            <Button
              variant="contained"
              onClick={onCreateTarget}
              disabled={!onCreateTarget}
            >
              {t("briefingsView.targetCreateCta", "Create target")}
            </Button>
          )}
        </Stack>
        <Alert severity="info">
          {t(
            "briefingsView.targetNotFound",
            "This associated campaign does not have targeting details yet."
          )}
        </Alert>
      </Paper>
    );
  }

  const renderDetailField = (
    fieldKey: string,
    label: string,
    value: string | number | null | undefined,
    isNumeric = false
  ): React.ReactNode => {
    const iconConfig = (() => {
      switch (fieldKey) {
        case "geography":
          return {
            icon: <PublicIcon fontSize="small" />,
            color: "success.main",
          };
        case "minimumAge":
        case "maximumAge":
        case "sex":
          return { icon: <PersonIcon fontSize="small" />, color: "info.main" };
        case "language":
          return {
            icon: <TranslateIcon fontSize="small" />,
            color: "primary.main",
          };
        case "dataAudience":
        case "interests":
          return {
            icon: <GroupsIcon fontSize="small" />,
            color: "secondary.main",
          };
        case "exclusions":
          return { icon: <BlockIcon fontSize="small" />, color: "error.main" };
        default:
          return null;
      }
    })();

    return (
      <Grid item xs={12} sm={6} md={4} key={label}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            transition: "box-shadow 0.2s",
            "&:hover": { boxShadow: theme.shadows[2] },
            bgcolor: theme.palette.background.default,
          }}
        >
          {iconConfig ? (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              color={iconConfig.color}
            >
              {iconConfig.icon}
              <Typography variant="subtitle2" fontWeight={600}>
                {label}
              </Typography>
            </Stack>
          ) : (
            <Typography
              variant="subtitle2"
              color="text.secondary"
              fontWeight={600}
            >
              {label}
            </Typography>
          )}
          <Typography
            variant="body2"
            sx={{ mt: "auto", wordBreak: "break-word" }}
            fontWeight={isNumeric ? 600 : 400}
          >
            {value === null || value === undefined || value === ""
              ? t("common.noData", "No data available")
              : isNumeric && typeof value === "number"
                ? numberFormatter.format(value)
                : value}
          </Typography>
        </Paper>
      </Grid>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${theme.palette.grey[200]}`,
        p: { xs: 2, md: 4 },
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Typography variant="h6" fontWeight={700}>
          {t("briefingsView.targetSectionTitle", "Audience targeting")}
        </Typography>
        {isEditable && (
          <Stack direction="row" spacing={1}>
            <Tooltip
              title={t("briefingsView.targetDeleteCta", "Delete target")}
            >
              <span>
                <IconButton
                  color="error"
                  onClick={onDeleteTarget}
                  disabled={!onDeleteTarget}
                  sx={{ "&:hover": { bgcolor: "error.main", color: "white" } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t("briefingsView.targetEditCta", "Edit target")}>
              <span>
                <IconButton
                  color="primary"
                  onClick={onEditTarget}
                  disabled={!onEditTarget}
                  sx={{ bgcolor: "action.hover" }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        )}
      </Stack>
      <Grid container spacing={2}>
        {renderDetailField(
          "sex",
          t("briefingsView.target.sex", "Sex"),
          target.sex
        )}
        {renderDetailField(
          "minimumAge",
          t("briefingsView.target.minimumAge", "Minimum age"),
          target.minimumAge,
          true
        )}
        {renderDetailField(
          "maximumAge",
          t("briefingsView.target.maximumAge", "Maximum age"),
          target.maximumAge,
          true
        )}
        {renderDetailField(
          "language",
          t("briefingsView.target.language", "Language"),
          getLanguageLabel(target.language, t)
        )}
        {renderDetailField(
          "geography",
          t("briefingsView.target.geography", "Geography"),
          target.geography ?? "-"
        )}
        {renderDetailField(
          "technology",
          t("briefingsView.target.technology", "Technology"),
          target.technology ?? "-"
        )}
        {renderDetailField(
          "interests",
          t("briefingsView.target.interests", "Interests"),
          target.interests ?? "-"
        )}
        {renderDetailField(
          "dataAudience",
          t("briefingsView.target.dataAudience", "Data audience"),
          target.dataAudience ?? "-"
        )}
        {renderDetailField(
          "exclusions",
          t("briefingsView.target.exclusions", "Exclusions"),
          target.exclusions ?? "-"
        )}
        {renderDetailField(
          "notes",
          t("briefingsView.target.notes", "Notes"),
          target.notes ?? "-"
        )}
        {target.imageUrl && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">
              {t("briefingsView.target.image", "Attachment Image")}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <img
                src={target.imageUrl}
                alt="Target attachment"
                style={{
                  height: 200,
                  width: "auto",
                  borderRadius: 4,
                  objectFit: "cover",
                }}
              />
            </Box>
          </Grid>
        )}
        {renderDetailField(
          "addedOn",
          t("briefingsView.addedOn", "Added on"),
          dayjs(target.createdAt).format("YYYY-MM-DD HH:mm:ss")
        )}
        {renderDetailField(
          "updatedOn",
          t("briefingsView.updatedOn", "Last updated"),
          dayjs(target.updatedAt).format("YYYY-MM-DD HH:mm:ss")
        )}
      </Grid>
    </Paper>
  );
};

export default AssociatedCampaignTargetSection;
